import { useEffect, useState } from 'react';
import { companyRequest } from '@/company/api';
import { Button } from '@/components/ui/button';
import { useNamespaceCopy } from '@/i18n/ns';
import shareCopy from '@/i18n/ns/share-chip';
import panelsCopy from '@/i18n/ns/artifact-panels';
import { formatCount } from '@/i18n/format';
import { SessionChangedError } from '@/lib/session-guard';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { CanvasHeaderTools } from '../canvas-footer';
import { useIntlLocale } from '../intl-locale';
import { useAppSharing } from './sharing';
import { ShareTrigger } from '../share-sheet/share-sheet';
import type { AppShareChoice, AppSharingState } from '../../../src/app-data/app-policy/sharing.mjs';

type Selection = Pick<AppShareChoice, 'artifactId' | 'versionId' | 'revisionId'>;

/** A new committed selection retires the old chip and its pending dialog. */
export function AppSharingChip({
  selection,
  title,
  onChanged,
  refreshKey,
}: {
  selection: Selection;
  title?: string;
  /** After a change: the saved view is re-read in place, so its rights and Unshare are current. */
  onChanged?: () => void;
  refreshKey?: object;
}) {
  const selected = {
    artifactId: selection.artifactId,
    versionId: selection.versionId,
    revisionId: selection.revisionId,
  };
  return (
    <SelectedSharingChip
      key={JSON.stringify(selected)}
      selection={selected}
      title={title}
      onChanged={onChanged}
      refreshKey={refreshKey}
    />
  );
}

/** Read the native App audience, then open the same Who/What prepare-confirm flow as the App page. */
function SelectedSharingChip({
  selection,
  title,
  onChanged,
  refreshKey,
}: {
  selection: Selection;
  title?: string;
  onChanged?: () => void;
  refreshKey?: object;
}) {
  const session = useSessionBoundFetch();
  const copy = useNamespaceCopy(shareCopy);
  const panels = useNamespaceCopy(panelsCopy);
  const locale = useIntlLocale();
  const [attempt, setAttempt] = useState(0);
  const [read, setRead] = useState<{
    session: typeof fetch;
    state: AppSharingState | null;
    failed: boolean;
  } | null>(null);
  useEffect(() => {
    let alive = true;
    setRead(null);
    const query = new URLSearchParams({
      versionId: selection.versionId,
      revisionId: selection.revisionId,
    });
    void companyRequest<AppSharingState>(
      `/api/v1/company/apps/${encodeURIComponent(selection.artifactId)}/sharing?${query}`,
      'GET',
      undefined,
      session,
    )
      .then((state) => {
        if (
          ['artifactId', 'versionId', 'revisionId'].some(
            (key) => state.saved?.[key as keyof Selection] !== selection[key as keyof Selection],
          )
        )
          throw new Error('sharing_selection_changed');
        if (alive) setRead({ session, state, failed: false });
      })
      .catch((error) => {
        if (alive && !(error instanceof SessionChangedError))
          setRead({ session, state: null, failed: true });
      });
    return () => {
      alive = false;
    };
  }, [
    selection.artifactId,
    selection.versionId,
    selection.revisionId,
    session,
    attempt,
    refreshKey,
  ]);
  const state = read?.session === session ? read.state : null;
  const sharing = useAppSharing({
    selection,
    session,
    canShare: state?.canShare === true,
    canUnshare: false,
    changed: () => {
      setAttempt((value) => value + 1);
      onChanged?.();
    },
    title,
  });
  const label =
    state &&
    (state.visibility === 'private'
      ? copy.onlyYou
      : state.visibility === 'company'
        ? copy.company
        : formatCount(copy.peopleCount, state.audience.length, locale));
  return (
    <CanvasHeaderTools>
      <span
        data-testid="share-chip-host"
        data-phase={state ? 'ready' : read?.failed ? 'failed' : 'loading'}
      >
        {state ? (
          sharing.onShare ? (
            <ShareTrigger
              visibility={state.visibility}
              audienceLabel={label ?? undefined}
              testId="share-chip"
              onClick={sharing.onShare}
            />
          ) : (
            <span data-testid="share-chip-readonly">{label}</span>
          )
        ) : read?.session === session && read.failed ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="share-chip-retry"
            onClick={() => setAttempt((value) => value + 1)}
          >
            {panels.retry}
          </Button>
        ) : null}
        {sharing.overlay}
      </span>
    </CanvasHeaderTools>
  );
}
